/**
 * ErrorBoundary.jsx
 *
 * Catches any unhandled render error in the React tree so the app
 * never goes completely blank. Shows a friendly UI with:
 *   • A "Try again" button  (reloads the page)
 *   • A "Go to home" link   (escapes to / in case of deep route crash)
 *   • The error message + stack (collapsed by default, expandable)
 *
 * Must be a class component — React error boundaries cannot be
 * written as function components (as of React 18).
 *
 * Usage (already wired in index.js):
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */

import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:  false,
      error:     null,
      errorInfo: null,
      showDetail: false,
    };
  }

  /* ── Lifecycle: called when a descendant throws ── */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    /* Store component stack for display */
    this.setState({ errorInfo });

    /* Log to console — easy to find in browser devtools / server logs */
    console.error("╔══ ErrorBoundary caught an error ══╗");
    console.error("Error:", error);
    console.error("Component stack:", errorInfo?.componentStack);
    console.error("╚═══════════════════════════════════╝");

    /* If you add Sentry / LogRocket later, call it here:
       Sentry.captureException(error, { extra: errorInfo });       */
  }

  reload  = () => window.location.reload();
  goHome  = () => { window.location.href = "/"; };
  toggle  = () => this.setState(s => ({ showDetail: !s.showDetail }));

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, errorInfo, showDetail } = this.state;
    const msg   = error?.message || "An unexpected error occurred.";
    const stack = errorInfo?.componentStack || "";

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>

          {/* Icon */}
          <div style={styles.icon}>⚠️</div>

          {/* Heading */}
          <h1 style={styles.heading}>Something went wrong</h1>
          <p style={styles.sub}>
            The app ran into an unexpected error. Your data is safe —
            this is a display issue only.
          </p>

          {/* Error message pill */}
          <div style={styles.errPill}>
            <span style={styles.errLabel}>Error</span>
            <span style={styles.errMsg}>{msg}</span>
          </div>

          {/* Action buttons */}
          <div style={styles.actions}>
            <button onClick={this.reload} style={styles.btnPrimary}>
              ↺ Reload page
            </button>
            <button onClick={this.goHome} style={styles.btnSecondary}>
              ← Go to home
            </button>
          </div>

          {/* Expandable stack trace */}
          <button onClick={this.toggle} style={styles.detailToggle}>
            {showDetail ? "▲ Hide technical detail" : "▼ Show technical detail"}
          </button>

          {showDetail && (
            <pre style={styles.stack}>
              <strong>{error?.toString()}</strong>
              {stack}
            </pre>
          )}

          <p style={styles.hint}>
            If this keeps happening, try clearing your browser cache or
            contact your system administrator.
          </p>
        </div>
      </div>
    );
  }
}

/* ── Inline styles — no Tailwind dependency so this works
   even if CSS fails to load ── */
const styles = {
  overlay: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#F9FAFB",
    padding: "24px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    boxShadow: "0 8px 40px rgba(0,0,0,0.10)",
    border: "1px solid #E5E7EB",
    padding: "40px 36px",
    maxWidth: 520,
    width: "100%",
    textAlign: "center",
  },
  icon: {
    fontSize: 48,
    marginBottom: 16,
    lineHeight: 1,
  },
  heading: {
    fontSize: 22,
    fontWeight: 800,
    color: "#111827",
    margin: "0 0 10px",
    fontFamily: "'Playfair Display', serif",
  },
  sub: {
    fontSize: 14,
    color: "#6B7280",
    margin: "0 0 20px",
    lineHeight: 1.6,
  },
  errPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "8px 14px",
    marginBottom: 24,
    maxWidth: "100%",
    textAlign: "left",
    wordBreak: "break-word",
  },
  errLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#DC2626",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    flexShrink: 0,
  },
  errMsg: {
    fontSize: 12,
    color: "#7F1D1D",
    fontFamily: "monospace",
  },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  btnPrimary: {
    background: "#2563EB",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 22px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  btnSecondary: {
    background: "#F3F4F6",
    color: "#374151",
    border: "none",
    borderRadius: 10,
    padding: "10px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  detailToggle: {
    background: "none",
    border: "none",
    color: "#9CA3AF",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "'Inter', system-ui, sans-serif",
    marginBottom: 10,
    textDecoration: "underline",
    textUnderlineOffset: 3,
  },
  stack: {
    background: "#1F2937",
    color: "#D1FAE5",
    borderRadius: 10,
    padding: "14px 16px",
    fontSize: 11,
    textAlign: "left",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 260,
    overflowY: "auto",
    marginBottom: 16,
    lineHeight: 1.6,
    fontFamily: "monospace",
  },
  hint: {
    fontSize: 11,
    color: "#9CA3AF",
    margin: 0,
    lineHeight: 1.5,
  },
};
